import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@apollo/react-hooks';

import {
  Typography,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table
} from '@material-ui/core';
import StarBorderRoundedIcon from '@material-ui/icons/StarBorderRounded';

import {
  CircularLoader,
} from 'components';

import TableSortLabel from '@material-ui/core/TableSortLabel';
import Pagination from 'components/pagination/Pagination';

import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';

import BookingsDetailsDialogDialog from 'components/booking-details-dialog/BookingDetailsDialog';

import { RENTAL_ITEMS_BOOKINGS } from 'graphql/queries/bookings';
import useStyles from './styles';
import {
    getRentalItemsBookingTotalInfo,
    BookedAddOns
} from 'utils/bookings/prepareBookingsData';
import ListPopover from 'components/list-popover/ListPopover';
import { InfoOutlined } from '@material-ui/icons';
import { useSyncDialogContent } from 'hooks/useSyncDialodContent';

const RENTAL_ITEMS_TYPE = 'Rental Items';
const headingsConfig = classes => {
  return [
    {
      label: 'Reference • Date',
      align: 'left',
      sortBy: 'creation_time',
      cellClassName: classes.referenceCell
    },
    {
      label: 'Booking name',
      sortBy: 'guest_first_name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Rental Items',
      sortBy: 'title',
      cellClassName: classes.unitCell,
      align: 'left'
    },
    {
      label: 'NO. of Units',
      sortBy: 'units',
      cellClassName: classes.guestsCell
    },
    {
      label: 'From Date',
      sortBy: 'from_date',
      cellClassName: classes.checkInCell,
      align: 'left'
    },
    {
      label: '',
      cellClassName: classes.arrowCell,
      align: 'left',
      sortBy: ''
    },
    {
      label: 'To Date',
      sortBy: 'to_date',
      cellClassName: classes.checkOutCell,
      align: 'left'
    },
    {
      label: 'Platform',
      sortBy: 'platform',
      cellClassName: classes.platformCell,
      align: 'left'
    },
    {
      label: 'Add-ons',
      cellClassName: classes.addOnsCell,
      sortBy: '',
      align: 'left'
    },
    {
      label: 'Notes',
      cellClassName: classes.notesCell,
      sortBy: '',
      align: 'left'
    },
    { label: 'cancellation Time', sortBy: '', align: 'right' }
  ];
};

function CancelledBookingsListTable({ handledStatus, businessOwner, search }) {
  const classes = useStyles();
  const [t] = useTranslation();

  const [open, setOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState({});
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('creation_time');
  const [page, setPage] = React.useState(0);

  const openInfoDialog = booking => {
    setOpen(true);
    setDialogContent(booking);
  };

  const onClose = () => {
    setOpen(false);
  };

  const variables = {
    search: search || '',
    isCanceled: true,
    businessOwnerId:
      businessOwner.selectedBusinessOwner.ownerId ||
      businessOwner.selectedBusinessOwner.id
  };

  const { data,loading: isLoading, refetch } = useQuery(RENTAL_ITEMS_BOOKINGS, {
    variables
  });
  

  useSyncDialogContent(data, setDialogContent);

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = property => event => {
    handleRequestSort(event, property);
  };

  React.useEffect(() => {
    if (search === undefined) return;
    setPage(0);
  }, [search]);

  React.useEffect(() => {
    setPage(0);
    refetch();
  }, [handledStatus, refetch]);

  return (
    <>
      <CircularLoader
        isLoading={isLoading}
        loaderClassName={classes.loaderClassName}
      />
      <TableContainer component="div" classes={{ root: classes.container }}>
        <Table className={classes.table} aria-label="simple table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              {headingsConfig(classes).map(heading => (
                <TableCell
                  key={heading.label}
                  align={heading.align}
                  style={{ paddingBottom: 15 }}
                  className={classNames(
                    classes.tableCell,
                    heading.cellClassName
                  )}
                  sortDirection={orderBy === heading.label ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === heading.sortBy && orderBy !== ''}
                    direction={orderBy === heading.sortBy ? order : 'asc'}
                    onClick={createSortHandler(heading.sortBy)}
                    className={classes.heading}
                    disabled={heading.sortBy === ''}
                    hideSortIcon={heading.sortBy === ''}
                  >
                    {t(heading.label)}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data &&
              data.rentalItemsBookings.map(booking => (
                <TableRow
                  key={booking.id}
                  classes={{ root: classes.tableRow }}
                  onClick={e => {
                    if (booking.deactivationBooking) return;

                    e.stopPropagation();
                    openInfoDialog(booking);
                  }}
                  className={classNames({
                    [classes.disabledArea]: booking.deactivationBooking
                  })}
                >
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.referenceCell
                    )}
                    align="left"
                  >
                    <Typography>{booking.referenceNumber}</Typography>
                    <Typography>{booking.creationTime}</Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(classes.tableCell, classes.nameCell)}
                    align="left"
                  >
                    <Typography className={classes.nameCell}>
                      {booking.deactivationBooking
                        ? 'Deactivation Booking'
                        : `${booking.guestFirstName} ${booking.guestLastName}`}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(classes.tableCell, classes.unitCell)}
                    align="left"
                  >
                    <div className={classes.unitContent}>
                      {booking.bookingType === RENTAL_ITEMS_TYPE && (
                        <StarBorderRoundedIcon
                          fontSize="small"
                          className={classes.unitIcon}
                        ></StarBorderRoundedIcon>
                      )}

                      <Typography className={classes.title}>
                        {booking.bookingType === RENTAL_ITEMS_TYPE &&
                          booking.rentalItems.title}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell
                    className={classNames(
                      classes.tableCell,
                      classes.guestsCell
                    )}
                    align="left"
                  >
                    <div className={classes.guestsContent}>
                      {booking.units.length === 0
                        ? '—'
                        : booking.units[0].unitsNumber}
                    </div>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.checkInCell
                    )}
                    align="left"
                  >
                    {booking.bookingType === RENTAL_ITEMS_TYPE &&
                      booking.toDate &&
                      (booking.rentalItems?.pricesType === 'pricesHourlyForm'
                        ? booking.startTime
                          ? moment(booking.startTime).format('YYYY-MM-DD HH:mm')
                          : moment(booking.fromDate).format('YYYY-MM-DD')
                        : moment(booking.fromDate).format('YYYY-MM-DD'))}
                  </TableCell>

                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(classes.tableCell)}
                    align="left"
                  >
                    <TrendingFlatIcon
                      style={{ fill: '#9B9B9B', width: '25px', height: '20px' }}
                    />
                  </TableCell>

                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.checkInCell
                    )}
                    align="left"
                  >
                    {booking.bookingType === RENTAL_ITEMS_TYPE &&
                      booking.toDate &&
                      (booking.rentalItems?.pricesType === 'pricesHourlyForm'
                        ? booking.endTime
                          ? moment(booking.endTime).format('YYYY-MM-DD HH:mm')
                          : moment(booking.toDate).format('YYYY-MM-DD')
                        : moment(booking.toDate).format('YYYY-MM-DD'))}
                  </TableCell>

                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.platformCell
                    )}
                    align="left"
                  >
                    {t(booking.platform)}
                  </TableCell>

                  <TableCell
                    style={{ paddingBottom: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.addOnsCell
                    )}
                    align="left"
                  >
                    {booking.bookedAddOns.length <= 2 && (
                      <Typography className={classes.addOnTitle}>
                        {BookedAddOns(booking.bookedAddOns)}
                      </Typography>
                    )}
                    {booking.bookedAddOns.length > 2 && (
                      <Typography className={classes.addOnTitle}>
                        {BookedAddOns(booking.bookedAddOns.slice(0, 2))} ...
                      </Typography>
                    )}

                    {booking.bookedAddOns.length > 2 && (
                      <ListPopover
                        list={
                          <ul className={classes.list}>
                            {booking.bookedAddOns.slice(2).map((o, index) => {
                              return (
                                <li key={index}>
                                  {o.addOnCount} x {o.addOn[0].title}
                                </li>
                              );
                            })}
                          </ul>
                        }
                      >
                        <NumberIcon number={booking.bookedAddOns.length - 2} />
                      </ListPopover>
                    )}
                  </TableCell>

                  <TableCell
                    style={{ paddingBottom: 5 }}
                    className={classNames(classes.tableCell, classes.notesCell)}
                    align="left"
                  >
                    <ListPopover
                      maxWidth={300}
                      list={
                        <div className={classes.list} style={{ minWidth: 120 }}>
                          {booking.bookingNotes||
                            'There are no notes yet.'}
                        </div>
                      }
                    >
                      <InfoOutlined />
                    </ListPopover>
                  </TableCell>

                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classes.tableCell}
                    align="right"
                  >
                    <div
                     className={classNames(
                      classes.tableCell,
                    )}
                    >
                      <Typography>
                        {moment(booking.cancellationTime).format('YYYY-MM-DD')}
                      </Typography>
                      <Typography>
                        {moment(booking.cancellationTime).format('HH:mm')}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ))}

            <BookingsDetailsDialogDialog
              onClose={onClose}
              open={open}
              booking={dialogContent}
              bookingTotal={
                dialogContent.bookingType === RENTAL_ITEMS_TYPE
                  ? getRentalItemsBookingTotalInfo(dialogContent)
                  : null
              }
              refetchParams={{ query: data, variables }}
            />
          </TableBody>
        </Table>
        {data && (
          <Pagination
            page={page + 1}
            count={data.numberOfBookingsRentalItemsPages}
            onChange={onPageChangeHandler}
            amount={`${page * 11 + 1}-${Math.min(
              (page + 1) * 11,
              data.rentalItemsBookingsOverview.canceledBookingsNumber
            )}`}
            total={data.rentalItemsBookingsOverview.canceledBookingsNumber}
          />
        )}
      </TableContainer>
    </>
  );
}

const mapStateToProps = state => ({
  businessOwner: state.businessOwner
});

export default connect(mapStateToProps, null)(CancelledBookingsListTable);
